/**
 * Los errores del protocolo, contados como lo que son: sucesos.
 *
 * Antes cada pantalla de juego pintaba `state.error` como un párrafo rojo dentro del flujo. Eso
 * fallaba dos veces. Una, porque empujaba el contenido hacia abajo justo cuando hay algo que
 * pulsar. Y otra, más grave: **nadie lo limpiaba nunca**. Los tres únicos sitios que ponían el
 * error a nulo —una sala que llega, una pregunta que se abre, una respuesta aceptada— no ocurren
 * después de un PIN inexistente ni de un apodo rechazado, así que el párrafo se quedaba en
 * pantalla el resto de la sesión, mucho después de que hubiera dejado de ser cierto.
 *
 * Aquí se traduce **con los parámetros**, que hasta ahora se tiraban: el servidor manda el motivo
 * concreto por el que un apodo no vale, ya traducido en los tres idiomas, y el cliente lo
 * ignoraba para enseñar «Ese apodo no vale» a secas.
 */

import { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import type { AliasRejection, GameErrorKey } from '@clica/shared';
import { useAvisos } from '../components/sistema/avisos.js';

/**
 * El motivo del rechazo de un apodo, resuelto con un mapa y no interpolando la clave.
 *
 * Interpolar compilaría igual y dejaría de compilar cuando alguien añadiera un motivo nuevo sin
 * su traducción: el mapa obliga a añadirla en el mismo cambio.
 */
export const MOTIVOS_DE_APODO: Readonly<
  Record<AliasRejection, `game.aliasReason.${AliasRejection}`>
> = {
  empty: 'game.aliasReason.empty',
  tooShort: 'game.aliasReason.tooShort',
  tooLong: 'game.aliasReason.tooLong',
  looksLikeUrl: 'game.aliasReason.looksLikeUrl',
  profanity: 'game.aliasReason.profanity',
};

function esMotivoDeApodo(valor: string | undefined): valor is AliasRejection {
  return valor !== undefined && valor in MOTIVOS_DE_APODO;
}

export interface ErrorDePartida {
  readonly key: GameErrorKey;
  readonly params?: Record<string, string>;
  readonly seq: number;
}

/**
 * Convierte cada error nuevo en un aviso efímero.
 *
 * @param alFallar Qué hacer además de avisar. Lo usa el PIN inexistente para devolver a quien
 *   entró a la pantalla donde se teclea, en vez de dejarlo en una pantalla muerta.
 */
export function useErroresComoAvisos(
  error: ErrorDePartida | null,
  alFallar?: (key: GameErrorKey) => void,
): void {
  const { t } = useTranslation();
  const avisos = useAvisos();
  /** El último anunciado. Sin esto, cualquier repintado volvería a avisar de lo mismo. */
  const ultimo = useRef(0);
  const alFallarRef = useRef(alFallar);
  alFallarRef.current = alFallar;

  useEffect(() => {
    if (error === null || error.seq === ultimo.current) return;
    ultimo.current = error.seq;
    /*
     * El motivo concreto viaja en `params` y **se tiraba**.
     *
     * El servidor manda `{ reason: 'looksLikeUrl' }` con el apodo rechazado, `game.aliasReason.*`
     * está traducido en los tres idiomas desde el principio, y el cliente enseñaba «Ese apodo no
     * vale» a secas. Quien no sabe por qué no vale, prueba otro parecido.
     */
    const motivo = error.params?.['reason'];
    const detalle = esMotivoDeApodo(motivo) ? t(MOTIVOS_DE_APODO[motivo]) : null;
    avisos.fallo(detalle === null ? t(error.key) : `${t(error.key)} ${detalle}`);
    alFallarRef.current?.(error.key);
  }, [error, t, avisos]);
}
